// #503 — client-side unrecoverable-session signal.
//
// Staff auth is cookie-mode (lib/api.ts): the httpOnly session cookie
// authenticates every call, and on a 401 the API layer rotates it via
// POST /v1/auth/session/refresh and retries once. When BOTH the session cookie
// and the refresh have lapsed, nothing is recoverable — every request 401s.
//
// The problem this module solves: `claims` in AuthContext is hydrated once from
// GET /v1/auth/me and is NOT cleared by a failed apiFetch, so ProtectedRoute
// keeps rendering the app over a dead session — the user sees a live-looking
// page where every call fails (and it reads as "feature X broke" rather than
// "you were signed out"). The API layer fires this signal on an unrecoverable
// 401 so AuthProvider can drop auth state and let ProtectedRoute bounce to
// /login. Dependency-free (no React) so lib/api.ts imports it without a cycle.

type Listener = () => void;

const listeners = new Set<Listener>();

/** Subscribe to unrecoverable-session-expiry. Returns an unsubscribe fn. */
export function onSessionExpired(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/**
 * Signal that a staff (cookie-mode) request received a 401 that survived the
 * server-side refresh attempt — the session cannot be recovered without a new
 * login. Safe to call on every failed request; listeners are expected to be
 * idempotent (clearing already-null auth state is a no-op).
 */
export function notifySessionExpired(): void {
  for (const fn of listeners) {
    try {
      fn();
    } catch {
      /* one listener throwing must not stop the others */
    }
  }
}
